/**
 * Contraband discount handler
 * ---------------------------------------------------------------------------
 * Applies and clears Shopify cart-level discount codes via the Cart API
 * (/cart/update.js), shows an "applied" badge below the Apply button, refreshes
 * cart UI (drawer + /cart page), and clears the discount when a discounted line
 * item is removed so re-added products start at full price.
 */
(function () {
  var applyingDiscount = false;
  var clearingStaleDiscount = false;
  var lastCartItemCount = null;
  var USER_DISCOUNT_SESSION_KEY = 'contraband_user_discount';

  function getUserDiscountCode() {
    try {
      return sessionStorage.getItem(USER_DISCOUNT_SESSION_KEY) || '';
    } catch (e) {
      return '';
    }
  }

  function isUserDiscountApplied() {
    return Boolean(getUserDiscountCode());
  }

  function markUserDiscountApplied(code) {
    try {
      sessionStorage.setItem(USER_DISCOUNT_SESSION_KEY, String(code).trim());
    } catch (e) {}
  }

  function clearUserDiscountSession() {
    try {
      sessionStorage.removeItem(USER_DISCOUNT_SESSION_KEY);
    } catch (e) {}
  }

  function getSectionsToRefresh() {
    var sections = ['cart-drawer', 'cart-icon-bubble'];
    var cartItems = document.getElementById('main-cart-items');
    var cartFooter = document.getElementById('main-cart-footer');
    if (cartItems && cartItems.dataset.id) sections.push(cartItems.dataset.id);
    if (cartFooter && cartFooter.dataset.id) sections.push(cartFooter.dataset.id);
    return sections;
  }

  function getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  function discountIsActive(cart) {
    if (!cart) return false;
    if (typeof cart.total_discount === 'number' && cart.total_discount > 0) return true;
    if (cart.cart_level_discount_applications && cart.cart_level_discount_applications.length > 0) return true;

    if (cart.items && cart.items.length) {
      for (var i = 0; i < cart.items.length; i++) {
        var item = cart.items[i];
        if (item.line_level_discount_allocations && item.line_level_discount_allocations.length > 0) return true;
        if (item.original_line_price > item.final_line_price) return true;
        if (item.original_price > item.final_price) return true;
      }
    }

    return false;
  }

  function findMatchingItem(beforeItems, afterItem) {
    for (var i = 0; i < beforeItems.length; i++) {
      var beforeItem = beforeItems[i];
      if (
        String(beforeItem.variant_id) === String(afterItem.variant_id) ||
        String(beforeItem.id) === String(afterItem.id)
      ) {
        return beforeItem;
      }
    }
    return null;
  }

  function cartDiscountChanged(beforeCart, afterCart) {
    if (!afterCart) return false;
    if (!beforeCart) return discountIsActive(afterCart);

    if (discountIsActive(afterCart) && !discountIsActive(beforeCart)) return true;

    if (
      typeof afterCart.total_price === 'number' &&
      typeof beforeCart.total_price === 'number' &&
      afterCart.total_price < beforeCart.total_price
    ) {
      return true;
    }

    if (
      typeof afterCart.total_discount === 'number' &&
      typeof beforeCart.total_discount === 'number' &&
      afterCart.total_discount > beforeCart.total_discount
    ) {
      return true;
    }

    var afterItems = afterCart.items || [];
    var beforeItems = beforeCart.items || [];
    for (var i = 0; i < afterItems.length; i++) {
      var afterItem = afterItems[i];
      var beforeItem = findMatchingItem(beforeItems, afterItem);
      if (!beforeItem) continue;
      if (afterItem.final_price < beforeItem.final_price) return true;
      if (afterItem.final_line_price < beforeItem.final_line_price) return true;
    }

    // CHANGED: return false when no measurable discount delta (prevents false positives)
    return false;
  }

  function getDiscountApplication(cart) {
    if (cart.cart_level_discount_applications && cart.cart_level_discount_applications.length) {
      return cart.cart_level_discount_applications[0];
    }

    if (cart.items) {
      for (var i = 0; i < cart.items.length; i++) {
        var allocs = cart.items[i].line_level_discount_allocations;
        if (allocs && allocs.length && allocs[0].discount_application) {
          return allocs[0].discount_application;
        }
      }
    }

    return null;
  }

  /** CHANGED: Build (-X%) / (-$X) suffix from Shopify discount_application metadata. */
  function formatDiscountSuffixFromApp(app, cart) {
    if (!app) return '';

    // CHANGED: percentage discounts expose value_type === 'percentage' and value (e.g. 50)
    if (app.value_type === 'percentage' && app.value != null && app.value !== '') {
      return ' (-' + Math.round(parseFloat(app.value)) + '%)';
    }

    // CHANGED: fixed-amount discounts expose value_type === 'fixed_amount'
    if (app.value_type === 'fixed_amount') {
      var fixedAmount = app.total_allocated_amount || app.value || (cart && cart.total_discount);
      if (fixedAmount) return ' (-' + formatMoneyFromCents(fixedAmount) + ')';
    }

    return '';
  }

  /** CHANGED: Prefer discount_application fields; avoid inferring % from post-discount subtotals. */
  function inferDiscountSuffix(cart) {
    var app = getDiscountApplication(cart);
    var suffix = formatDiscountSuffixFromApp(app, cart);
    if (suffix) return suffix;

    // CHANGED: Last-resort fallback only when Shopify omits value_type on the application
    if (cart && cart.items_subtotal_price > 0 && cart.total_discount > 0) {
      var pct = Math.round((cart.total_discount / cart.items_subtotal_price) * 100);
      if (pct > 0) return ' (-' + pct + '%)';
    }

    return '';
  }

  function formatMoneyFromCents(cents) {
    if (window.Shopify && typeof Shopify.formatMoney === 'function') {
      return Shopify.formatMoney(cents);
    }
    return (cents / 100).toFixed(2);
  }

  function buildAppliedBadgeHTML(code, cart) {
    var suffix = inferDiscountSuffix(cart);
    return (
      '<span class="disc-applied-badge" data-disc-applied-badge role="status">' +
      '<span class="disc-applied-badge__icon svg-wrapper">' +
      '<svg viewBox="0 0 12 12" aria-hidden="true"><path fill="currentColor" fill-rule="evenodd" d="M7 0h3a2 2 0 0 1 2 2v3a1 1 0 0 1-.3.7l-6 6a1 1 0 0 1-1.4 0l-4-4a1 1 0 0 1 0-1.4l6-6A1 1 0 0 1 7 0m2 2a1 1 0 1 0 2 0 1 1 0 0 0-2 0" clip-rule="evenodd"/></svg>' +
      '</span>' +
      '<span class="disc-applied-badge__text">Discount applied: <strong>' +
      escapeHTML(code) +
      '</strong>' +
      escapeHTML(suffix) +
      '</span>' +
      '<button type="button" class="disc-applied-badge__remove" data-discount-remove aria-label="Remove discount code">' +
      '<svg viewBox="0 0 12 13" aria-hidden="true"><path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" fill="none" d="M8.486 9.33 2.828 3.67M2.885 9.385l5.544-5.77"/></svg>' +
      '</button>' +
      '</span>'
    );
  }

  function escapeHTML(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function showAppliedBadge(container, code, cart) {
    if (!container) return;
    var badges = container.querySelector('[data-disc-applied-badges]');
    if (!badges) return;
    badges.innerHTML = buildAppliedBadgeHTML(code, cart);
    badges.hidden = false;
  }

  function hideAppliedBadge(container) {
    if (!container) return;
    var badges = container.querySelector('[data-disc-applied-badges]');
    if (!badges) return;
    badges.innerHTML = '';
    badges.hidden = true;
  }

  function resetApplyButton(container) {
    var applyBtn = container.querySelector('[data-disc-apply]');
    var applyLabel = container.querySelector('[data-disc-apply-label]');
    if (applyBtn) applyBtn.disabled = false;
    if (applyLabel) applyLabel.textContent = 'Apply';
  }

  function showError(container, msg) {
    resetApplyButton(container);
    var errorBox = container.querySelector('[data-disc-error]');
    var successBox = container.querySelector('[data-disc-success]');
    if (successBox) successBox.hidden = true;
    if (errorBox) {
      errorBox.textContent = msg;
      errorBox.hidden = false;
    }
  }

  function resetDiscountInputs() {
    document.querySelectorAll('[data-disc-input]').forEach(function (input) {
      input.value = '';
    });
    document.querySelectorAll('.disc-inline').forEach(function (panel) {
      hideAppliedBadge(panel);
      hideMessages(panel);
      resetApplyButton(panel);
    });
  }

  function hideMessages(container) {
    var errorBox = container.querySelector('[data-disc-error]');
    var successBox = container.querySelector('[data-disc-success]');
    if (errorBox) errorBox.hidden = true;
    if (successBox) successBox.hidden = true;
  }

  function fetchCartJson() {
    return fetch('/cart.js', {
      headers: { Accept: 'application/json' },
    }).then(function (res) {
      return res.json();
    });
  }

  function openDiscountPanels() {
    document.querySelectorAll('.disc-inline').forEach(function (panel) {
      panel.open = true;
    });
  }

  function paintCartDrawerSection(cart) {
    var cartDrawerEl = document.querySelector('cart-drawer');
    if (!cartDrawerEl || !cart.sections || !cart.sections['cart-drawer']) return cartDrawerEl;

    var drawer = document.getElementById('CartDrawer');
    if (drawer) {
      drawer.innerHTML = getSectionInnerHTML(cart.sections['cart-drawer'], '#CartDrawer');
    }

    cartDrawerEl.classList.toggle('is-empty', cart.item_count === 0);

    var drawerItems = cartDrawerEl.querySelector('cart-drawer-items');
    if (drawerItems) {
      drawerItems.classList.toggle('is-empty', cart.item_count === 0);
    }

    if (typeof cartDrawerEl.rebindDrawerElements === 'function') {
      cartDrawerEl.rebindDrawerElements();
    }

    return cartDrawerEl;
  }

  function paintCartSections(cart, options) {
    options = options || {};
    if (!cart.sections) return;

    var discountPanelWasOpen =
      options.forceDiscountOpen ||
      (isUserDiscountApplied() && discountIsActive(cart)) ||
      Array.prototype.some.call(document.querySelectorAll('.disc-inline'), function (panel) {
        return panel.open;
      });

    var cartDrawerEl = document.querySelector('cart-drawer');
    var drawerWasOpen = cartDrawerEl && cartDrawerEl.classList.contains('active');

    paintCartDrawerSection(cart);

    var cartItems = document.getElementById('main-cart-items');
    if (cartItems && cartItems.dataset.id && cart.sections[cartItems.dataset.id]) {
      var itemsContents = cartItems.querySelector('.js-contents');
      if (itemsContents) {
        itemsContents.innerHTML = getSectionInnerHTML(cart.sections[cartItems.dataset.id], '.js-contents');
      }
      cartItems.classList.toggle('is-empty', cart.item_count === 0);
    }

    var cartFooter = document.getElementById('main-cart-footer');
    if (cartFooter && cartFooter.dataset.id && cart.sections[cartFooter.dataset.id]) {
      var parsedFooter = new DOMParser().parseFromString(cart.sections[cartFooter.dataset.id], 'text/html');
      var newBlocks = parsedFooter.querySelector('.cart__blocks');
      var currentBlocks = cartFooter.querySelector('.cart__blocks');
      if (newBlocks && currentBlocks) {
        currentBlocks.innerHTML = newBlocks.innerHTML;
      } else {
        var footerContents = cartFooter.querySelector('.js-contents');
        if (footerContents) {
          footerContents.innerHTML = getSectionInnerHTML(cart.sections[cartFooter.dataset.id], '.js-contents');
        }
      }
      cartFooter.classList.toggle('is-empty', cart.item_count === 0);
    }

    var bubble = document.getElementById('cart-icon-bubble');
    if (bubble && cart.sections['cart-icon-bubble']) {
      bubble.innerHTML = getSectionInnerHTML(cart.sections['cart-icon-bubble'], '.shopify-section');
    }

    if (drawerWasOpen && cartDrawerEl) {
      cartDrawerEl.classList.add('active', 'animate');
      document.body.classList.add('overflow-hidden');
    }

    if (discountPanelWasOpen) {
      openDiscountPanels();
    }
  }

  function fetchCartSections() {
    return fetch('/cart/update.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({
        updates: {},
        sections: getSectionsToRefresh(),
        sections_url: window.location.pathname,
      }),
    }).then(function (res) {
      return res.json();
    });
  }

  function refreshCartUI(appliedCode) {
    return fetchCartSections().then(function (cart) {
      paintCartSections(cart);

      if (appliedCode && discountIsActive(cart)) {
        markUserDiscountApplied(appliedCode);
        openDiscountPanels();
        document.querySelectorAll('.disc-inline').forEach(function (container) {
          showAppliedBadge(container, appliedCode, cart);
        });
      } else if (!discountIsActive(cart)) {
        clearUserDiscountSession();
        resetDiscountInputs();
      } else {
        restoreAppliedDiscountUI(cart);
      }

      return cart;
    });
  }

  function applyDiscountViaCartUpdate(code) {
    return fetch('/cart/update.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({ discount: code.trim() }),
    }).then(function (res) {
      return res.json();
    });
  }

  function applyDiscountCode(code, container) {
    applyingDiscount = true;
    hideMessages(container);
    var applyBtn = container.querySelector('[data-disc-apply]');
    var applyLabel = container.querySelector('[data-disc-apply-label]');
    if (applyBtn) applyBtn.disabled = true;
    if (applyLabel) applyLabel.textContent = 'Applying…';

    var beforeCart;

    return fetchCartJson()
      .then(function (cart) {
        beforeCart = cart;
        return applyDiscountViaCartUpdate(code);
      })
      .then(function (cart) {
        if (!cartDiscountChanged(beforeCart, cart)) {
          showError(container, "That code doesn't look valid, or it doesn't apply to the items in your cart.");
          return cart;
        }

        markUserDiscountApplied(code);

        return fetchCartSections().then(function (sectionsCart) {
          paintCartSections(sectionsCart, { forceDiscountOpen: true });
          document.querySelectorAll('.disc-inline').forEach(function (panel) {
            showAppliedBadge(panel, code, sectionsCart);
            resetApplyButton(panel);
          });
          return sectionsCart;
        });
      })
      .catch(function () {
        showError(container, "Couldn't confirm the code right now — please try again.");
      })
      .finally(function () {
        applyingDiscount = false;
      });
  }

  /** Clears the cart-level discount via the Cart API. */
  function clearCartDiscount() {
    return fetch('/cart/update.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({ discount: '' }),
    }).then(function (res) {
      return res.json();
    });
  }

  function removeDiscountCode() {
    return clearCartDiscount().then(refreshCartUI).then(function (cart) {
      clearUserDiscountSession();
      resetDiscountInputs();
      return cart;
    });
  }

  /** Clears cart discount when it was not applied via our Apply button this session. */
  function clearStaleSessionDiscount(cart) {
    if (clearingStaleDiscount || applyingDiscount) return Promise.resolve(cart);
    if (!cart || !discountIsActive(cart) || isUserDiscountApplied()) return Promise.resolve(cart);

    clearingStaleDiscount = true;

    return clearCartDiscount()
      .then(function () {
        return refreshCartUI();
      })
      .then(function (refreshedCart) {
        resetDiscountInputs();
        return refreshedCart || cart;
      })
      .finally(function () {
        clearingStaleDiscount = false;
      });
  }

  function restoreAppliedDiscountUI(cart) {
    if (!cart || !discountIsActive(cart) || !isUserDiscountApplied()) return;

    var code = getUserDiscountCode();
    if (!code) return;

    openDiscountPanels();
    document.querySelectorAll('.disc-inline').forEach(function (panel) {
      showAppliedBadge(panel, code, cart);
    });
  }

  function initDiscountState() {
    fetchCartJson()
      .then(function (cart) {
        lastCartItemCount = cart.item_count;
        if (!isUserDiscountApplied() && discountIsActive(cart)) {
          return clearStaleSessionDiscount(cart);
        }
        restoreAppliedDiscountUI(cart);
        return cart;
      })
      .catch(function () {});
  }

  function lineItemHadDiscount(line) {
    var lineItemEl =
      document.getElementById('CartItem-' + line) || document.getElementById('CartDrawer-Item-' + line);
    if (!lineItemEl) return false;
    if (lineItemEl.dataset.lineDiscounted === 'true') return true;
    return lineItemEl.querySelector('.discounts__discount, .disc-line-badge') !== null;
  }

  function shouldClearDiscountAfterRemove(line, cart) {
    if (lineItemHadDiscount(line)) return true;
    if (cart && cart.item_count === 0 && (discountIsActive(cart) || isUserDiscountApplied())) return true;
    return false;
  }

  function handleLineItemRemoved(line, cart, options) {
    options = options || {};
    if (!options.force && !shouldClearDiscountAfterRemove(line, cart)) {
      return Promise.resolve(cart);
    }

    return clearCartDiscount()
      .then(function () {
        return refreshCartUI();
      })
      .then(function (refreshedCart) {
        clearUserDiscountSession();
        resetDiscountInputs();
        return refreshedCart || cart;
      });
  }

  function clearDiscountAfterReAdd(cart) {
    clearingStaleDiscount = true;

    return clearCartDiscount()
      .then(function () {
        clearUserDiscountSession();
        resetDiscountInputs();
        return refreshCartUI();
      })
      .then(function (refreshedCart) {
        return refreshedCart || cart;
      })
      .finally(function () {
        clearingStaleDiscount = false;
      });
  }

  function onCartUpdate(event) {
    if (applyingDiscount || clearingStaleDiscount) return;

    var cart = event && event.cartData;
    if (!cart) return;

    var prevCount = lastCartItemCount;
    lastCartItemCount = cart.item_count;

    var isReAdd = prevCount === 0 && cart.item_count > 0;
    if (isReAdd && (discountIsActive(cart) || isUserDiscountApplied())) {
      clearDiscountAfterReAdd(cart);
      return;
    }

    if (!isUserDiscountApplied() && discountIsActive(cart)) {
      clearStaleSessionDiscount(cart);
      return;
    }

    if (!discountIsActive(cart)) {
      clearUserDiscountSession();
      resetDiscountInputs();
      return;
    }

    restoreAppliedDiscountUI(cart);
  }

  function onApplyClick(event) {
    var applyBtn = event.target.closest('[data-disc-apply]');
    if (!applyBtn) return;

    var container = applyBtn.closest('.disc-inline');
    if (!container) return;

    var input = container.querySelector('[data-disc-input]');
    var code = input ? input.value.trim() : '';

    if (!code) {
      showError(container, 'Please enter a discount code.');
      return;
    }

    container.open = true;
    applyDiscountCode(code, container);
  }

  function onApplyKeydown(event) {
    if (event.key !== 'Enter') return;
    var input = event.target.closest('[data-disc-input]');
    if (!input) return;
    event.preventDefault();
    var container = input.closest('.disc-inline');
    var applyBtn = container && container.querySelector('[data-disc-apply]');
    if (applyBtn) applyBtn.click();
  }

  function onRemoveChipClick(event) {
    var removeBtn = event.target.closest('[data-discount-remove]');
    if (!removeBtn) return;
    event.preventDefault();
    removeDiscountCode();
  }

  document.addEventListener('click', onApplyClick);
  document.addEventListener('keydown', onApplyKeydown);
  document.addEventListener('click', onRemoveChipClick);

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, onCartUpdate);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initDiscountState);
  } else {
    initDiscountState();
  }

  window.ContrabandDiscount = {
    clearCartDiscount: clearCartDiscount,
    refreshCartUI: refreshCartUI,
    paintCartDrawerSection: paintCartDrawerSection,
    lineItemHadDiscount: lineItemHadDiscount,
    shouldClearDiscountAfterRemove: shouldClearDiscountAfterRemove,
    handleLineItemRemoved: handleLineItemRemoved,
    clearStaleSessionDiscount: clearStaleSessionDiscount,
    discountIsActive: discountIsActive,
  };
})();
